import { supabase } from '../supabase/config';
import { logActivity } from '../utils/activityLogger';

const TABLE_NAME = 'discounts';

// Convert snake_case DB row to camelCase for app consumption
const toDiscount = (row) => {
    if (!row) return null;
    return {
        id: row.id,
        tenantId: row.tenant_id,
        title: row.title,
        description: row.description,
        isReward: row.is_reward,
        value: row.value,
        requiredPoints: row.required_points,
        minAmount: row.min_amount,
        totalLimit: row.total_limit,
        customerLimit: row.customer_limit,
        startDate: row.start_date,
        startTime: row.start_time,
        expiryDate: row.expiry_date,
        expiryTime: row.expiry_time,
        applicableDays: row.applicable_days || [0, 1, 2, 3, 4, 5, 6],
        type: row.type || 'percentage',
        target: row.target || 'all',
        loyaltyStatus: row.loyalty_status || [],
        visitRole: row.visit_role || [],
        selectedCustomers: row.selected_customers || [],
        productScope: row.product_scope || 'all',
        selectedProducts: row.selected_products || [],
        selectedCategories: row.selected_categories || [],
        campaignType: row.campaign_type || 'simple',
        buyItems: row.buy_items || [],
        getItems: row.get_items || [],
        buyQuantity: row.buy_quantity || 1,
        getQuantity: row.get_quantity || 1,
        bundleDiscountValue: row.bundle_discount_value || 100,
        sameItemMode: row.same_item_mode !== false,
        birthdayOnly: row.birthday_only || false,
        isActive: row.is_active,
        usageCount: row.usage_count,
        createdAt: row.created_at,
        updatedAt: row.updated_at,
        isCoupon: row.campaign_type === 'coupon' || row.campaign_type === 'coupon_bundle',
        isReward: row.is_reward === true,
    };
};

export const promotionsService = {
    // One-time fetch of a tenant's promotions (camelCase). Useful for
    // short-lived views (e.g. the manual order modal) that don't need realtime.
    fetchPromotions: async (effectiveTenantId) => {
        if (!effectiveTenantId) return [];
        try {
            const { data, error } = await supabase
                .from(TABLE_NAME)
                .select('*')
                .eq('tenant_id', effectiveTenantId);
            if (error) {
                console.error("Promosyonlar çekilirken hata:", error);
                return [];
            }
            return (data || []).map(toDiscount);
        } catch (error) {
            console.error("Promosyonlar çekilirken hata:", error);
            return [];
        }
    },

    subscribeToPromotions: (effectiveTenantId, isSuperAdmin, onUpdate) => {
        // Initial fetch
        const fetchData = async () => {
            try {
                let query = supabase.from(TABLE_NAME).select('*');
                if (!effectiveTenantId) {
                    if (isSuperAdmin) {
                        query = query.limit(1000);
                    } else {
                        onUpdate([]);
                        return;
                    }
                } else {
                    query = query.eq('tenant_id', effectiveTenantId);
                }
                const { data, error } = await query;
                if (error) {
                    console.error("Promosyonlar çekilirken hata:", error);
                    onUpdate([]);
                    return;
                }
                onUpdate((data || []).map(toDiscount));
            } catch (error) {
                console.error("Promosyonlar çekilirken hata:", error);
                onUpdate([]);
            }
        };

        fetchData();

        // Realtime subscription
        const filter = effectiveTenantId
            ? `tenant_id=eq.${effectiveTenantId}`
            : undefined;

        const channelConfig = {
            event: '*',
            schema: 'public',
            table: TABLE_NAME,
        };
        if (filter) {
            channelConfig.filter = filter;
        }

        const channel = supabase
            .channel(`discounts-${effectiveTenantId || 'all'}`)
            .on('postgres_changes', channelConfig, () => {
                // Re-fetch all data on any change for consistency
                fetchData();
            })
            .subscribe();

        // Return unsubscribe function
        return () => {
            supabase.removeChannel(channel);
        };
    },

    fetchMenu: async (effectiveTenantId) => {
        if (!effectiveTenantId) return null;
        try {
            const { data, error } = await supabase
                .from('menus')
                .select('*')
                .eq('tenant_id', effectiveTenantId)
                .single();

            if (error || !data) return null;

            const ensureArrays = (obj) => {
                if (!obj || typeof obj !== 'object' || obj instanceof Date) return obj;
                const keys = Object.keys(obj);
                const isNumericMap = keys.length > 0 && keys.every(k => !isNaN(k));
                if (isNumericMap) return keys.sort((a, b) => parseInt(a) - parseInt(b)).map(k => ensureArrays(obj[k]));
                const cleaned = {};
                keys.forEach(k => { cleaned[k] = ensureArrays(obj[k]); });
                return cleaned;
            };

            let result = {
                tenantId: data.tenant_id,
                menuName: data.menu_name,
                description: data.description,
                categories: data.categories,
                categoriesJson: data.categories_json,
                designRecommendation: data.design_recommendation,
                updatedAt: data.updated_at,
            };

            if (result.categoriesJson) {
                try { result.categories = JSON.parse(result.categoriesJson); }
                catch (e) { result.categories = []; }
            }

            result = ensureArrays(result);
            if (!Array.isArray(result.categories)) result.categories = [];
            return result;
        } catch (error) {
            console.error("Menu cekilirken hata:", error);
            return null;
        }
    },

    savePromotion: async (editingId, formData, effectiveTenantId, userData, user) => {
        const now = new Date().toISOString();
        const data = {
            tenant_id: effectiveTenantId,
            title: formData.title,
            description: formData.description,
            is_reward: formData.isReward,
            value: formData.isReward ? null : Number(formData.value),
            required_points: formData.isReward ? Number(formData.requiredPoints) : null,
            min_amount: Number(formData.minAmount) || 0,
            total_limit: formData.totalLimit ? Number(formData.totalLimit) : null,
            start_date: formData.startDate || null,
            start_time: formData.startTime || '00:00',
            expiry_date: formData.expiryDate || null,
            expiry_time: formData.expiryTime || '23:59',
            applicable_days: formData.applicableDays || [0, 1, 2, 3, 4, 5, 6],
            type: formData.type || null,
            target: formData.target || 'all',
            loyalty_status: formData.loyaltyStatus || [],
            visit_role: formData.visitRole || [],
            selected_customers: formData.selectedCustomers || [],
            product_scope: formData.productScope || 'all',
            selected_products: formData.selectedProducts || [],
            selected_categories: formData.selectedCategories || [],
            campaign_type: formData.campaignType || 'simple',
            buy_items: formData.buyItems || [],
            get_items: formData.getItems || [],
            buy_quantity: Number(formData.buyQuantity) || 1,
            get_quantity: Number(formData.getQuantity) || 1,
            bundle_discount_value: Number(formData.bundleDiscountValue) || 100,
            same_item_mode: formData.sameItemMode !== false,
            birthday_only: formData.birthdayOnly || false,
            customer_limit: formData.birthdayOnly ? 1 : (formData.customerLimit ? Number(formData.customerLimit) : null),
            is_active: formData.isActive !== undefined ? formData.isActive : true,
            updated_at: now,
        };

        if (editingId) {
            const { error } = await supabase
                .from(TABLE_NAME)
                .update(data)
                .eq('id', editingId);
            if (error) throw error;
        } else {
            const { error } = await supabase
                .from(TABLE_NAME)
                .insert({ ...data, created_at: now, usage_count: 0 });
            if (error) throw error;
        }

        await logActivity(
            effectiveTenantId,
            userData?.email || user.email,
            user.id,
            editingId ? (formData.isReward ? 'UPDATE_REWARD' : 'UPDATE_DISCOUNT') : (formData.isReward ? 'CREATE_REWARD' : 'CREATE_DISCOUNT'),
            `${formData.isReward ? 'Odul' : 'Indirim'} ${editingId ? 'guncellendi' : 'olusturuldu'}: ${data.title}`
        );
    },

    deletePromotion: async (item, effectiveTenantId, userData, user) => {
        const { error } = await supabase
            .from(TABLE_NAME)
            .delete()
            .eq('id', item.id);
        if (error) throw error;

        await logActivity(
            effectiveTenantId,
            userData?.email || user.email,
            user.id,
            item.isReward ? 'DELETE_REWARD' : 'DELETE_DISCOUNT',
            `${item.isReward ? 'Odul' : 'Indirim'} silindi: ${item.title}`
        );
    },
    
    toggleStatus: async (item, effectiveTenantId, userData, user) => {
        const newStatus = item.isActive === false ? true : false;
        const { error } = await supabase
            .from(TABLE_NAME)
            .update({ is_active: newStatus })
            .eq('id', item.id);
        if (error) throw error;

        await logActivity(
            effectiveTenantId,
            userData?.email || user.email,
            user.id,
            item.isReward ? 'UPDATE_REWARD_STATUS' : 'UPDATE_DISCOUNT_STATUS',
            `${item.isReward ? 'Odul' : 'Indirim'} durumu ${newStatus ? 'Aktif' : 'Pasif'} olarak guncellendi: ${item.title}`
        );
    }
};
